import { useEffect } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { ListSlackChannelsResponse } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const SLACK_CHANNELS_QUERY_KEY = 'slack-channels';

const getSlackChannelsQueryKey = (orgId?: string, workspaceId?: string) =>
  [
    SLACK_CHANNELS_QUERY_KEY,
    ...(orgId !== undefined ? [orgId] : []),
    ...(workspaceId !== undefined ? [workspaceId] : []),
  ] as const;

const getSlackChannelsQueryOptions = ({
  orgId,
  workspaceId,
  enabled,
}: {
  orgId: string | undefined;
  workspaceId: string;
  enabled: boolean;
}) =>
  infiniteQueryOptions<ListSlackChannelsResponse>({
    queryKey: getSlackChannelsQueryKey(orgId, workspaceId),
    queryFn: async ({ pageParam }): Promise<ListSlackChannelsResponse> => {
      const { data } = await apiClient.notifications.listSlackChannels({
        id: workspaceId,
        cursor: (pageParam as string) || undefined,
      });
      return data;
    },
    enabled: enabled && !!workspaceId,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

type UseGetSlackChannelsParams = {
  workspaceId: string;
  enabled?: boolean;
};

export const useGetSlackChannels = ({
  workspaceId,
  enabled = true,
}: UseGetSlackChannelsParams) => {
  const { currentOrganization } = useOrganizations();

  const {
    data,
    error,
    isLoading,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isRefetching,
    dataUpdatedAt,
  } = useInfiniteQuery(
    getSlackChannelsQueryOptions({
      orgId: currentOrganization?.id,
      workspaceId,
      enabled,
    })
  );

  // Automatically fetch all pages
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isLoading) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]);

  const channels = flatMap(data?.pages, (page) => page?.channels ?? []);

  return {
    data: { channels },
    error,
    isLoading,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isRefetching,
    dataUpdatedAt,
  };
};
